/**
 * Vanilla tooltip module — replaces Bootstrap 3 tooltip JS.
 *
 * Two activation modes:
 *   1. Explicit: elements with [data-tooltip-toggle] + title attribute
 *   2. Auto: all <a> elements with a non-empty title attribute
 *      (mirrors the legacy `$("a").tooltip()` behaviour)
 *
 * Reads placement from `data-placement` attribute (top | bottom | left | right).
 * Defaults to "top".
 *
 * Renders tooltip using Bootstrap 3 CSS classes so existing styles apply:
 *   .tooltip.top.in > .tooltip-arrow + .tooltip-inner
 *
 * Exposes window.__tooltip for the one programmatic use case:
 *   window.__tooltip.set(element, title, placement)  — attach/update tooltip
 *   window.__tooltip.destroy(element)                 — remove tooltip
 */

// Guard against double-initialisation when multiple packs import this module
if ((window as any).__tooltipInitialised) {
  // already running — bail out so listeners aren't registered twice
} else {
(window as any).__tooltipInitialised = true;

const TOGGLE_ATTR = "data-tooltip-toggle";
const TOOLTIP_CLASS = "tooltip";
const SHOW_DELAY = 200;
const HIDE_DELAY = 100;

let activeTooltip: HTMLElement | null = null;
let activeTarget: HTMLElement | null = null;
let showTimer: ReturnType<typeof setTimeout> | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;

// Store original titles so we can suppress native browser tooltip
const storedTitles = new WeakMap<HTMLElement, string>();

function getTitle(el: HTMLElement): string {
  if (storedTitles.has(el)) return storedTitles.get(el)!;
  return el.getAttribute("title") || "";
}

function storeTitle(el: HTMLElement): void {
  if (storedTitles.has(el)) return;
  const title = el.getAttribute("title") || "";
  if (title) {
    storedTitles.set(el, title);
    // Remove native title to prevent browser tooltip showing alongside ours
    el.removeAttribute("title");
  }
}

function restoreTitle(el: HTMLElement): void {
  const title = storedTitles.get(el);
  if (title) {
    // Only restore if element is still in the DOM (AJAX content may have been replaced)
    if (el.isConnected) {
      el.setAttribute("title", title);
    }
    storedTitles.delete(el);
  }
}

function getPlacement(el: HTMLElement): string {
  return el.getAttribute("data-placement") || "top";
}

function createTooltipElement(text: string, placement: string): HTMLElement {
  const tip = document.createElement("div");
  tip.className = `${TOOLTIP_CLASS} ${placement} in`;
  tip.setAttribute("role", "tooltip");

  const arrow = document.createElement("div");
  arrow.className = "tooltip-arrow";

  const inner = document.createElement("div");
  inner.className = "tooltip-inner";
  inner.textContent = text;

  tip.appendChild(arrow);
  tip.appendChild(inner);
  return tip;
}

function positionTooltip(tip: HTMLElement, target: HTMLElement, placement: string): void {
  // Hide during measurement to prevent flash at (0,0)
  tip.style.visibility = "hidden";
  tip.style.position = "absolute";
  document.body.appendChild(tip);

  const targetRect = target.getBoundingClientRect();
  const tipRect = tip.getBoundingClientRect();
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;

  let top = 0;
  let left = 0;

  switch (placement) {
    case "top":
      top = targetRect.top + scrollY - tipRect.height;
      left = targetRect.left + scrollX + (targetRect.width - tipRect.width) / 2;
      break;
    case "bottom":
      top = targetRect.bottom + scrollY;
      left = targetRect.left + scrollX + (targetRect.width - tipRect.width) / 2;
      break;
    case "left":
      top = targetRect.top + scrollY + (targetRect.height - tipRect.height) / 2;
      left = targetRect.left + scrollX - tipRect.width;
      break;
    case "right":
      top = targetRect.top + scrollY + (targetRect.height - tipRect.height) / 2;
      left = targetRect.right + scrollX;
      break;
  }

  tip.style.top = `${top}px`;
  tip.style.left = `${left}px`;
  tip.style.visibility = "";
}

function show(target: HTMLElement): void {
  clearTimers();

  const title = getTitle(target);
  if (!title) return;

  showTimer = setTimeout(() => {
    hide();

    storeTitle(target);
    const placement = getPlacement(target);
    const tip = createTooltipElement(title, placement);
    positionTooltip(tip, target, placement);

    activeTooltip = tip;
    activeTarget = target;
  }, SHOW_DELAY);
}

function hide(): void {
  clearTimers();

  if (activeTooltip) {
    activeTooltip.remove();
    activeTooltip = null;
  }
  if (activeTarget) {
    restoreTitle(activeTarget);
    activeTarget = null;
  }
}

function clearTimers(): void {
  if (showTimer) {
    clearTimeout(showTimer);
    showTimer = null;
  }
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}

function findTarget(el: EventTarget | null): HTMLElement | null {
  if (!(el instanceof HTMLElement)) return null;
  return el.closest<HTMLElement>(`[${TOGGLE_ATTR}]`) || el.closest<HTMLElement>("a[title]");
}

// --- event listeners ---

document.addEventListener("mouseenter", (e: MouseEvent) => {
  const target = findTarget(e.target);
  if (target) show(target);
}, true); // capture phase to work with delegation

document.addEventListener("mouseleave", (e: MouseEvent) => {
  // Use findTarget first, but also check activeTarget as fallback
  // (title attr may have been removed by storeTitle, making a[title] unmatchable)
  const target = findTarget(e.target);
  if (target || activeTarget) {
    clearTimers();
    hideTimer = setTimeout(hide, HIDE_DELAY);
  }
}, true);

document.addEventListener("focusin", (e: FocusEvent) => {
  const target = findTarget(e.target);
  if (target) show(target);
});

document.addEventListener("focusout", () => {
  clearTimers();
  hideTimer = setTimeout(hide, HIDE_DELAY);
});

// --- programmatic API for _pricing_type_js.html.erb ---

interface TooltipAPI {
  set(el: HTMLElement | JQuery, title: string, placement?: string): void;
  destroy(el: HTMLElement | JQuery): void;
}

function unwrapJQuery(el: HTMLElement | JQuery): HTMLElement[] {
  // Handle jQuery objects
  if (el && typeof (el as any).each === "function") {
    const elements: HTMLElement[] = [];
    (el as any).each(function (this: HTMLElement) {
      elements.push(this);
    });
    return elements;
  }
  return [el as HTMLElement];
}

const tooltipAPI: TooltipAPI = {
  set(el, title, placement = "top") {
    for (const elem of unwrapJQuery(el)) {
      storedTitles.set(elem, title);
      elem.setAttribute("data-placement", placement);
      elem.setAttribute(TOGGLE_ATTR, "");
    }
  },
  destroy(el) {
    for (const elem of unwrapJQuery(el)) {
      storedTitles.delete(elem);
      elem.removeAttribute(TOGGLE_ATTR);
      if (activeTarget === elem) hide();
    }
  },
};

(window as any).__tooltip = tooltipAPI;

} // end of double-init guard
